<script setup lang="ts">
import { computed, type Ref } from "vue";
import { useRouter } from "vue-router";
import { format } from "date-fns";
import Skeleton from "primevue/skeleton";
import SharedCopyableField from "./SharedCopyableField.vue";
import type { CrudRouteMeta } from "src/router/types";

const props = defineProps<{
  label: string;
  id: string;
  embeddedData: Record<string, unknown>;
  entityRoute: string | null;
  stacked?: boolean;
  hideFields?: string[];
}>();

const emit = defineEmits<{
  navigate: [];
}>();

const router = useRouter();

function getScalarEntries(obj: Record<string, unknown>, hidden: Set<string>) {
  return Object.entries(obj).filter(([key, value]) => {
    if (key === "id" || key.endsWith("_id")) return false;
    if (hidden.has(key)) return false;
    if (Array.isArray(value)) return false;
    if (typeof value === "object" && value !== null) return false;
    return value !== null && value !== undefined;
  });
}

const hiddenFields = computed(() => new Set(props.hideFields ?? []));
const embeddedScalars = computed(() => getScalarEntries(props.embeddedData, hiddenFields.value));

let fetchedData: Ref<any> | null = null;
let fetchPending: Ref<boolean> | null = null;

if (props.entityRoute) {
  const listRouteName = props.entityRoute.endsWith("-detail")
    ? props.entityRoute.replace(/-detail$/, "")
    : props.entityRoute;
  const targetRoute = router.getRoutes().find((r) => r.name === listRouteName);
  const fetchOneHook = (targetRoute?.meta as CrudRouteMeta | undefined)?.requests
    ?.fetchOne;
  if (fetchOneHook) {
    const result = fetchOneHook(computed(() => props.id));
    fetchedData = result.data;
    fetchPending = result.isPending;
  }
}

const isLoading = computed(() => {
  if (!fetchPending) return false;
  return fetchPending.value && embeddedScalars.value.length === 0;
});

const displayFields = computed(() => {
  let entries: [string, unknown][];
  if (fetchedData?.value) {
    const fetched = getScalarEntries(fetchedData.value as Record<string, unknown>, hiddenFields.value);
    entries = fetched.length > 0 ? fetched : embeddedScalars.value;
  } else {
    entries = embeddedScalars.value;
  }

  const fields = entries.slice(0, 5).map(([key, value]) => ({
    label: formatLabel(key),
    value: formatDisplayValue(value),
  }));

  if (!hiddenFields.value.has("id")) {
    fields.push({ label: "ID", value: props.id });
  }
  return fields;
});

function formatLabel(fieldName: string): string {
  return fieldName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return format(new Date(value.replace(" ", "T")), "d MMM yyyy");
    } catch {
      return value;
    }
  }
  return String(value);
}
</script>

<template>
  <div class="relation-card">
    <div
      class="relation-card-header"
      :class="{ 'relation-card-header--link': entityRoute }"
      @click="entityRoute && emit('navigate')"
    >
      <span>{{ label }}</span>
      <i v-if="entityRoute" class="pi pi-external-link text-xs" />
    </div>

    <div
      v-if="isLoading"
      :class="['relation-card-fields', { 'relation-card-fields--stacked': stacked }]"
    >
      <Skeleton v-for="i in 3" :key="i" width="100%" height="0.875rem" style="grid-column: 1 / -1" />
    </div>

    <div
      v-else
      :class="['relation-card-fields', { 'relation-card-fields--stacked': stacked }]"
    >
      <div v-for="field in displayFields" :key="field.label" class="relation-card-field-pair">
        <span class="relation-card-field-label">{{ field.label }}</span>
        <SharedCopyableField :value="field.value" class="text-sm min-w-0" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.relation-card {
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid var(--p-content-border-color);
  transition: border-color 0.2s;
}

.relation-card:hover {
  border-color: var(--p-primary-color);
}

.relation-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-text-muted-color);
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.relation-card-header--link {
  cursor: pointer;
  transition: color 0.2s;
}

.relation-card-header--link:hover {
  color: var(--p-primary-color);
}

.relation-card-fields {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 0.75rem;
  align-items: baseline;
}

.relation-card-field-pair {
  display: contents;
}

.relation-card-fields--stacked {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
}

.relation-card-fields--stacked .relation-card-field-pair {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.relation-card-field-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}
</style>
